"use client";

import { logger } from "@utilities/logger";
import { toast } from "@/app/_providers/toast";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@ui/components/core/Dialog";
import { cn } from "@ui/lib/utils";
import { RotateCcwIcon } from "lucide-react";
import { useTranslations } from "next-intl";
import React from "react";

import type { Preset, Sound } from "../types";

import { useSoundboard } from "../state/reducer";
import { CreateAccountButton } from "./CreateAccountReusableButton";
import { SoundBoardButton } from "./SoundBoardButton";

type File = {
  blob: Blob;
  name: string;
};
async function downloadFilesAsZip(filename: string, files: File[]) {
  try {
    // Load JSZip dynamically to avoid SSR issues
    const JSZip = (await import("jszip")).default;
    const zip = new JSZip();
    for (const file of files) {
      zip.file(file.name, file.blob);
    }

    // Generate and download zip file
    const content = await zip.generateAsync({ type: "blob" });
    const downloadUrl = URL.createObjectURL(content);

    const link = document.createElement("a");
    link.href = downloadUrl;
    link.download = `${filename}.zip`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(downloadUrl);
  } catch (err) {
    logger.error(err instanceof Error ? err : null, "Error downloading sounds");
  }
}

async function downloadPreset(preset: Preset) {
  // Create array of sound URLs from preset
  const sounds = preset.sounds.filter((slot): slot is Sound => slot.type !== "placeholder");
  if (!sounds.length) throw new Error("No sounds found in preset");
  // Split sounds into regular URLs and base64 encoded
  const soundsWithUrls = sounds.filter(s => s.url.startsWith("https"));
  const base64Sounds = sounds.filter(s => s.url.startsWith("data:"));

  // Convert both types to File objects
  const urlFiles = await Promise.all(
    soundsWithUrls.map(async sound => {
      const response = await fetch(sound.url);
      const blob = await response.blob();
      return {
        blob,
        name: `${sound.name}.mp3`,
      };
    }),
  );

  const base64Files = base64Sounds.map(sound => {
    // Extract base64 data after the comma
    const base64Data = sound.url.split(",")[1];
    const binaryData = atob(base64Data);

    // Convert binary string to Uint8Array
    const bytes = new Uint8Array(binaryData.length);
    for (let i = 0; i < binaryData.length; i++) {
      bytes[i] = binaryData.charCodeAt(i);
    }

    // Detect audio format by analyzing binary header (magic bytes)
    const detectAudioFormat = (bytes: Uint8Array): { extension: string; mimeType: string } => {
      if (bytes.length < 12) return { extension: "mp3", mimeType: "audio/mpeg" };

      // Check for WAV format (RIFF header)
      if (bytes[0] === 0x52 && bytes[1] === 0x49 && bytes[2] === 0x46 && bytes[3] === 0x46) {
        return { extension: "wav", mimeType: "audio/wav" };
      }

      // Check for OGG format (which contains Opus)
      if (bytes[0] === 0x4f && bytes[1] === 0x67 && bytes[2] === 0x67 && bytes[3] === 0x53) {
        // Look for "OpusHead" marker to confirm it's Opus
        const dataStr = String.fromCharCode(...bytes.slice(0, Math.min(100, bytes.length)));
        if (dataStr.includes("OpusHead")) {
          return { extension: "opus", mimeType: "audio/opus" };
        }
      }

      // Check for MP3 format
      // MP3 can start with ID3 tag or MP3 frame sync
      if (
        // ID3 tag
        (bytes[0] === 0x49 && bytes[1] === 0x44 && bytes[2] === 0x33) ||
        // MP3 frame sync (0xFF followed by 0xFB, 0xFA, 0xF3, or 0xF2)
        (bytes[0] === 0xff && (bytes[1] & 0xe0) === 0xe0)
      ) {
        return { extension: "mp3", mimeType: "audio/mpeg" };
      }

      // Default to MP3 if we can't detect the format
      return { extension: "mp3", mimeType: "audio/mpeg" };
    };

    const { extension, mimeType } = detectAudioFormat(bytes);

    return {
      blob: new Blob([bytes], { type: mimeType }),
      name: `${sound.name}.${extension}`,
    };
  });

  // Combine both types of files
  const allFiles = [...urlFiles, ...base64Files];

  // Download as zip if we have any files
  await downloadFilesAsZip(preset.name + "-sounds", allFiles);
}

const SignupDialog = ({ open, onOpenChange }: { onOpenChange: (open: boolean) => void; open: boolean }) => {
  const t = useTranslations("ClientKeys.soundboard");
  const { state } = useSoundboard();

  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      <DialogContent className='px-24 py-40 text-center sm:max-w-[525px]'>
        <div className='mx-auto flex flex-col space-y-24 px-32'>
          <DialogHeader className='w-full space-y-5'>
            <DialogTitle className='heading-03 mb-2 text-2xl'>{t("download_preset_button.signup_title")}</DialogTitle>
            <DialogDescription className='description-01 mb-6 text-sm'>
              {t("download_preset_button.signup_description")}
            </DialogDescription>
          </DialogHeader>
          <div className='max-w-xxl mx-auto'>
            <CreateAccountButton className='rounded-xl py-12' state={state}>
              {t("download_preset_button.signup_button")}
            </CreateAccountButton>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

function InverseButton({ children, ...props }: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      className='font-soundboard f-ui-04 w-full flex-1 rounded-sm border border-default px-16 py-12 disabled:opacity-50'
      {...props}
    >
      {children}
    </button>
  );
}

export const DownloadPresetButton = ({
  full,
  variant = "default",
}: {
  full?: boolean;
  variant?: "default" | "inverse";
}) => {
  const { state } = useSoundboard();
  const t = useTranslations("ClientKeys.soundboard");
  const [showSignupDialog, setShowSignupDialog] = React.useState(false);

  const handleClick = () => {
    if (!state.user) {
      setShowSignupDialog(true);
      return;
    }

    const preset = state.presets[state.activePresetId ?? ""];
    if (!preset) return;

    toast.promise(downloadPreset(preset), {
      loading: t("download_preset_button.loading"),
      success: t("download_preset_button.success"),
      error: t("download_preset_button.error"),
    });
  };

  const content = (
    <>
      {full && <RotateCcwIcon className='' size={24} />}
      <p className='font-soundboard f-ui-04'>{t("download_preset_button.label")}</p>
    </>
  );

  return (
    <>
      {variant === "default" ? (
        <SoundBoardButton
          className={cn("control-button py-8 text-inverse", !full ? "flex-1" : "aspect-1x1")}
          fixedSize={false}
          onClick={handleClick}
        >
          {content}
        </SoundBoardButton>
      ) : (
        <InverseButton onClick={handleClick}>{content}</InverseButton>
      )}
      <SignupDialog onOpenChange={setShowSignupDialog} open={showSignupDialog} />
    </>
  );
};
